import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./Form.css";
import { requiredFieldClass } from "../lib/requiredFieldClass";
import { applicationSuccessMessage } from "../lib/applicationSuccessMessage";

const REASONS = [
  {/*}
    { value: "BEREAVEMENT", label: "忌引(家族の不幸)" },
    { value: "MARRIAGE", label: "結婚" },
    { value: "CHILDBIRTH", label: "出産" },
    { value: "CHILDCARE", label: "子供看護" },
    { value: "kaigo", label: "介護" },
    { value: "fresh", label: "リフレッシュ" },
    { value: "newyear", label: "年末年始" },
    { value: "SERI", label: "生理休暇" },
    { value: "BIRTHDAY", label: "バースデイ休暇"},
    { value: "OTHER", label: "その他" },
  */}
];

const DRAFT_KEY = 'draft_special_leave';

const isBereavementReason = (reason) =>
  reason === "BEREAVEMENT" || String(reason || "").includes("忌引");

const SpecialLeaveForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reason: "BEREAVEMENT",
    start_date: "",
    end_date: "",
    reason_text: "",
  });

  // 会社設定の特別休暇リスト（null=読込中/未設定）
  const [specialList, setSpecialList] = useState(null);

	const [minDate, setMinDate] = useState("");

  // ===== 最低日計算=====
  useEffect(() => {
    const today = new Date();
    today.setDate(today.getDate() + 1);

    if (isBereavementReason(formData.reason)) {
      setMinDate(today.toISOString().split("T")[0]);
    } else {
      const min = new Date(today);
      min.setDate(min.getDate() + 29);
      setMinDate(min.toISOString().split("T")[0]);
    }
  }, [formData.reason]);

  // 会社設定から特別休暇リストを取得
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/company-info/");
        const list = res?.data?.special_leave_list;
        if (!cancelled) setSpecialList(Array.isArray(list) ? list : []);
      } catch (_) {
        if (!cancelled) setSpecialList([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 選択肢：会社設定があればそれを使い、なければ既定のリスト
  const reasonOptions = useMemo(() => {
    if (Array.isArray(specialList) && specialList.length > 0) {
      return specialList
        .filter((it) => (it.label || "").trim() !== "")
        .map((it) => ({ value: it.label, label: it.label, content: it.content || "" }));
    }
    return REASONS.map((r) => ({ ...r, content: "" }));
  }, [specialList]);

  // 現在の選択肢に reason が無ければ先頭に合わせる
  useEffect(() => {
    if (reasonOptions.length && !reasonOptions.some((o) => o.value === formData.reason)) {
      setFormData((prev) => ({ ...prev, reason: reasonOptions[0].value }));
    }
  }, [reasonOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // 選択中の理由に対応する規程の条文（ホバーで表示する内容）
  const selectedContent = useMemo(
    () => reasonOptions.find((o) => o.value === formData.reason)?.content || "",
    [reasonOptions, formData.reason]
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData(prev => ({ ...prev, ...parsed }));
      }
    } catch (_) {}
  }, []);

  const handleSaveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    alert('一時保存しました。');
  };

  const handleDeleteDraft = async () => {
    const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
    if (!confirmDelete) return;

    setLoading(true);
    try {
      if (id) {
        await api.delete(`/api/applications/drafts/${id}/`);
      }        
      // ローカルでも削除
      localStorage.removeItem(DRAFT_KEY);
      alert('下書きを削除しました。');
      navigate('/applications');
    } catch (error) {
      console.error('下書きの削除に失敗:', error);
      alert('下書きの削除に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => { if (onBack) onBack(); else navigate("/applications"); };

  const canSubmit = useMemo(
    () => Boolean(formData.start_date && formData.end_date),
    [formData.start_date, formData.end_date]
  );

  // ===== Input change =====
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ===== Submit =====
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.start_date || !formData.end_date) {
      alert("期間を入力してください。");
      return;
    }

    if (formData.start_date > formData.end_date) {
      alert("開始日は終了日より前でなければなりません");
      return;
    }

    const payload = {
        start_date: formData.start_date,
        end_date : formData.end_date,
        reason : formData.reason,
        reason_text: "",
    }

    const confirmed = window.confirm("特別休暇申請を送信してもよろしいですか？");
    if (!confirmed) return;

    try {
      await api.post("/forms/submit_special_leave/", payload);
      localStorage.removeItem(DRAFT_KEY);
      alert(
        applicationSuccessMessage("特別休暇申請が正常に提出されました。", [
          "申請一覧から内容・状態を確認できます。",
        ])
      );
      navigate("/applications");
    } catch (error) {
      if (error.response?.data) {
        alert(JSON.stringify(error.response.data));
      } else {
        alert("送信に失敗しました");
      }
    }
  };

  return (
    <div className="container form-container">
      <h3>特別休暇申請</h3>

      <form onSubmit={handleSubmit}>

        
        {/* 開始日 */}
        <div className="mb-3">
          <label className="form-label">開始日</label>
          <input
            type="date"
            name="start_date"
            className={requiredFieldClass(formData.start_date)}
            value={formData.start_date}
            min={minDate}
            onChange={handleChange}
            required
          />
          {minDate && formData.reason !== "BEREAVEMENT" && (
            <small className="text-muted form-note">
              ※ 1ヶ月前までに申請してください。
            </small>
          )}
        </div>

        {/* 終了日 */}
        <div className="mb-3">
          <label className="form-label">終了日</label>
          <input
            type="date"
            name="end_date"
            className={requiredFieldClass(formData.end_date)}
            value={formData.end_date}
            min={formData.start_date || minDate}
            onChange={handleChange}
            required
          />
        </div>

        {/* 理由（会社マスタの特別休暇リストから選択。選択すると下に規程の条文を表示） */}
        <div className="mb-3">
          <label className="form-label">理由</label>
          <select
            name="reason"
            className="form-control"
            value={formData.reason}
            onChange={handleChange}
          >
            {reasonOptions.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {/* 選択した理由の規程内容（別枠のコメントとして表示） */}
        {selectedContent ? (
          <div
            className="mb-3"
            style={{
              background: "#f8f9fb",
              border: "1px solid #d8dee6",
              borderRadius: 8,
              padding: "12px 14px",
              fontSize: "0.9rem",
              lineHeight: 1.7,
              color: "#333",
              whiteSpace: "pre-wrap",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#0f1419" }}>
              {formData.reason} の規程内容
            </div>
            {selectedContent}
          </div>
        ) : null}


        {/* ボタン */}
        <div className="button-group">
          <button type="submit" className="btn-submit" disabled={!canSubmit}>申請</button>
          <button type="button" className="btn-draft" onClick={handleSaveDraft}>一時保存</button>
          {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} disabled={loading}>下書削除</button>}
          <button type="button" className="btn-back" onClick={handleBack}>
            {onBack && !isFirstForm ? '前へ' : '戻る'}
          </button>
          {isMultiForm && onNext && (
            <button type="button" className="btn-nav" onClick={onNext}>
              {isLastForm ? '完了' : '次へ'}
            </button>
          )}
        </div>
      </form>
    </div>
  );    
};

export default SpecialLeaveForm;